/** Diagonal pixel step between consecutively cascaded windows. */
export const CASCADE_STEP = 24;

/**
 * Smallest gap kept between a placed window and the far edge of the available
 * area, so a cascaded window never sits flush against (or past) the boundary.
 */
export const MIN_MARGIN = 16;

/**
 * Computes the diagonal offset for the next cascaded window. Each successive
 * window steps `CASCADE_STEP` further down-right; once another step would push
 * the window past the available area the offset wraps back to 0, so windows
 * fan out and then restack instead of marching off-screen.
 *
 * Shared by native-popup (`computeNextNativeBounds`) and virtual-window
 * (`computeNextVirtualBounds`) placement so both cascade identically — the only
 * difference between the two is the available metric (screen vs. viewport) and
 * the centring origin, which stay with each caller.
 *
 * @example
 * ```ts
 * computeCascadeOffset(0, 1280, 800, 640, 480); // 0 — first window, no offset
 * ```
 */
export function computeCascadeOffset(
    openCount: number,
    availableWidth: number,
    availableHeight: number,
    contentWidth: number,
    contentHeight: number,
): number {
    const horizontalSlack = Math.max(
        0,
        availableWidth - contentWidth - MIN_MARGIN,
    );
    const verticalSlack = Math.max(
        0,
        availableHeight - contentHeight - MIN_MARGIN,
    );
    const maxStepsHorizontal = Math.max(
        1,
        Math.floor(horizontalSlack / CASCADE_STEP),
    );
    const maxStepsVertical = Math.max(
        1,
        Math.floor(verticalSlack / CASCADE_STEP),
    );
    const wrapAfter = Math.max(
        1,
        Math.min(maxStepsHorizontal, maxStepsVertical),
    );

    return (openCount % (wrapAfter + 1)) * CASCADE_STEP;
}
